"use client";

import * as React from "react";
import Link from "next/link";
import { Smartphone, Swords, Globe, Share } from "lucide-react";
import { cn } from "@/lib/utils";
import { SECTION_TITLE } from "@/components/ui/typography";
import { getAppField, extractAppId, extractCountryCode } from "@/lib/app-data-helpers";
import { calculateDaysAgo, buildAppStoreUrl } from "@/lib/app-detail-utils";
import { AppMetrics } from "@/components/appstore/app-metrics";
import { AppScreenshots } from "@/components/appstore/app-screenshots";
import { AppFirstPartyScreens } from "@/components/appstore/first-party-screens";
import { AppAds } from "@/components/appstore/app-ads";
import { AppInAppPurchases } from "@/components/appstore/app-in-app-purchases";
import { AppKeywordRanks } from "@/components/appstore/app-keyword-ranks";
import { AppReviewsSection } from "@/components/appstore/app-reviews";
import { AppRatingsCard } from "@/components/appstore/app-ratings-card";
import { AppPriceHistory } from "@/components/appstore/app-price-history";
import { useAppPriceHistory } from "@/hooks/use-app-price-history";
import { recentPriceChange } from "@/lib/app-detail/price-history";
import { useAppOverview } from "@/hooks/use-app-overview";
import { AppDescription } from "@/components/appstore/app-description";
import { AppWhatsNew } from "@/components/appstore/app-whats-new";
import { AppInformation } from "@/components/appstore/app-information";
import { McpOfferStrip } from "@/components/mcp-offer/mcp-offer-strip";
import { getCategoryNameById } from "@/lib/constants";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMounted } from "@/hooks/use-theme-config";
import { useMonitors } from "@/hooks/use-monitors";
import { useAuth } from "@/providers/auth-provider";
import dynamic from "next/dynamic";
import { ChartCard } from "@/components/ui/chart-card";
// Below the screenshots and drawn from client fetches, so the server HTML only
// ever held its loading card; the chart code loads after hydration.
// It mounts after hydration only: rendered during SSR, even an ssr:false
// dynamic() puts its chunk in the first-load <script> tags.
const PerformanceCardLoading = () => <ChartCard title="Rank history" terminalCode="RANK.HISTORY" isLoading className="mb-8" />;
const AppPerformanceCard = dynamic(() => import("@/components/appstore/app-performance-card").then((m) => m.AppPerformanceCard), {
  ssr: false,
  loading: PerformanceCardLoading,
});
const noopSubscribe = () => () => {};
// Phone tabs only, opened by a tap. A require() here bundled the whole
// competitors sidebar (and its ranking table) into every first load.
const AppDetailSidebar = dynamic(() => import("@/components/app-detail-sidebar"), { ssr: false });
// Loaded on the first share tap: it is closed on every page load.
const ShareDialog = dynamic(() => import("@/components/ui/share-dialog"), { ssr: false });
import { shareNatively } from "@/lib/native-share";
import { track } from "@/lib/analytics/client";
import { AppHeader } from "@/components/AppHeader";
import type { AppDetailData } from "@/components/app-detail-modal";
import { LiquidGlassDock, LiquidGlassDockGlobalStyles } from "@/components/mobile/LiquidGlassDock";

type AppDetailMainProps = {
  appId: number;
  data: AppDetailData;
  isLoadingMetadata: boolean;
  showAdvanced: boolean;
  setShowAdvanced: (updater: ((v: boolean) => boolean) | boolean) => void;
  error?: string | null;
};

export function AppDetailMain({ appId, data, isLoadingMetadata, showAdvanced, setShowAdvanced, error }: AppDetailMainProps) {
  const isMobile = useIsMobile();
  const hydrated = React.useSyncExternalStore(noopSubscribe, () => true, () => false);
  const { user } = useAuth();
  const { byKey, add, remove } = useMonitors();
  const [shareOpen, setShareOpen] = React.useState(false);
  // Stays mounted after the first open so the close animation still plays.
  const [shareUsed, setShareUsed] = React.useState(false);
  if (shareOpen && !shareUsed) setShareUsed(true);
  const openShare = async () => {
    const result = await shareNatively({ title: document.title, url: window.location.href });
    if (result === 'shared') track('page_shared', { method: 'native_sheet', section: 'app' });
    if (result === 'unsupported') {
      track('page_shared', { method: 'dialog', section: 'app' });
      setShareOpen(true);
    }
  };
  const [mobileTab, setMobileTab] = React.useState<'info' | 'competitors' | 'rankings'>('info');

  // Extract core data using helpers
  const currentAppId = extractAppId(data, appId);
  const currentCountry = extractCountryCode(data);
  // Header and information-card fields Sensor Tower shows for free (SPEC 03); null until the route answers, and every field is optional.
  const { data: overview, isLoading: isLoadingOverview } = useAppOverview(currentAppId, currentCountry);
  // The header pill reads the whole history whatever range the card is showing, because
  // a change is news for 90 days and a 30d view would hide one that is 40 days old (SPEC 26).
  const priceHistory = useAppPriceHistory(currentAppId, currentCountry, 'all');
  const priceChange = React.useMemo(() => {
    const change = recentPriceChange(priceHistory.data);
    return change ? { after: change.after, date: change.date, currency: priceHistory.data.meta.currency, rose: change.after > change.before } : null;
  }, [priceHistory.data]);
  const key = `${currentAppId}:${currentCountry}`;
  const isMonitored = byKey.has(key);
  const appStoreUrl = buildAppStoreUrl(data, appId);

  // Extract dates
  const relRaw = getAppField<string>(data, 'release_date');
  const updRaw = getAppField<string>(data, 'current_version_release_date');

  // "Nd ago" is read off the viewer's clock after hydration: in the cached HTML it would change every
  // day and make each regeneration a new ISR write. The exact dates still render on the server.
  const mounted = useMounted();
  const releaseDaysAgo = React.useMemo(() => (mounted ? calculateDaysAgo(relRaw) : null), [mounted, relRaw]);
  const lastReleaseDaysAgo = React.useMemo(() => (mounted ? calculateDaysAgo(updRaw) : null), [mounted, updRaw]);

  return (
    <>
      <AppHeader
        variant="main"
        appIcon={getAppField<string>(data, 'icon_url')}
        appName={getAppField<string>(data, 'name') || ""}
        subtitle={getAppField<string>(data, 'subtitle')}
        developerName={getAppField<string>(data, 'developer_name') || ""}
        developerId={getAppField<number>(data, 'developer_id') || (data as any)?.artistId}
        appStoreUrl={appStoreUrl}
        releaseDaysAgo={releaseDaysAgo}
        lastReleaseDaysAgo={lastReleaseDaysAgo}
        releaseDate={relRaw}
        lastReleaseDate={updRaw}
        genres={(data as any)?.genres || null}
        fileSizeBytes={(data as any)?.fileSizeBytes || null}
        languages={getAppField<string[]>(data, 'languages')}
        contentRating={(data as any)?.contentAdvisoryRating || (data as any)?.trackContentRating || getAppField<string>(data, 'content_rating') || overview?.contentRating}
        categories={overview?.categories ?? null}
        topCountries={overview?.topCountries ?? null}
        advertisedOn={overview?.advertisedOn ?? null}
        sourceAsOf={overview?.sourceAsOf.appSt ?? null}
        isLoadingOverview={isLoadingOverview}
        priceChange={priceChange}
        platforms={
          overview
            ? { os: overview.os, ipad: overview.tabletScreenshotUrls.length > 0, appleWatch: overview.appleWatch, imessage: overview.imessage }
            : null
        }
        isLoadingMetadata={isLoadingMetadata}
        isMobile={isMobile}
        mobileTab={mobileTab}
        isMonitored={isMonitored}
        user={user}
        addPending={add.isPending}
        removePending={remove.isPending}
        onMonitorToggle={() => {
          if (isMonitored) {
            remove.mutate({ app_id: currentAppId, country_code: currentCountry });
          } else {
            add.mutate({ app_id: currentAppId, country_code: currentCountry });
          }
        }}
        onShareOpen={openShare}
        competitorsHref={`/competitors?app=${currentAppId}&country=${currentCountry}`}
      />

      {/* Info content (header above is always shown); sections conditional for mobile */}
      {(!isMobile || mobileTab === 'info') && (
        <>
          <div className="mb-2">
            <AppMetrics
              variant="figures"
              rankingPosition={getAppField<number>(data, 'ranking_position') ?? 0}
              previousRankingPosition={getAppField<number>(data, 'previous_ranking_position')}
              ratingCount={getAppField<number>(data, 'rating_count')}
              ratingAverage={getAppField<number>(data, 'average_rating')}
              revenueEstimate={getAppField<number>(data, 'revenue_estimate')}
              downloadsEstimate={getAppField<number>(data, 'downloads_estimate')}
              isLoading={isLoadingMetadata}
              // The server seed has no estimate keys; the refetched core slice always carries them (null when unknown).
              estimatesLoading={!!data && !('revenue_estimate' in data)}
              categoryName={(() => {
                const catId = getAppField<string>(data, 'category_id');
                return catId ? getCategoryNameById(parseInt(catId)) || undefined : undefined;
              })()}
              columns={isMobile ? 2 : 4}
              rankingCreatedAt={getAppField<string>(data, 'ranking_created_at')}
              ratingsCreatedAt={getAppField<string>(data, 'ratings_created_at')}
              revenueCreatedAt={getAppField<string>(data, 'revenue_created_at')}
            />
          </div>
          {/* One row under the figures: the MCP offer, and the Shortcut link on the right, so the page still fits one screenshot. */}
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <McpOfferStrip placement="app_page">
              {`Ask Claude or Cursor about ${getAppField<string>(data, 'name') || 'this app'}: ranks, revenue estimates, reviews and ads.`}
            </McpOfferStrip>
            <Link
              href="/shortcut"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground sm:gap-1.5 sm:text-[13px]"
            >
              <Share className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" />
              {/* "straight" drops on phones so the line stays on one row. */}
              <span>See these numbers <span className="hidden sm:inline">straight </span>from the App Store with the Shortcut →</span>
            </Link>
          </div>
          <div className="mb-8">
            <AppScreenshots
              screenshots={getAppField<string[]>(data, 'screenshot_urls')}
              tabletScreenshots={overview?.tabletScreenshotUrls ?? null}
              isLoading={isLoadingMetadata}
            />
          </div>

          {/* Our own captured in-app screens — non-blocking; self-hides when the
              app has no published catalog (renders nothing, so no empty space). */}
          <AppFirstPartyScreens appId={currentAppId} className="mb-8" />

          {/* Ads the publisher runs across the Meta/Google/Apple ad libraries; self-hides when empty. */}
          <AppAds appId={currentAppId} appName={getAppField<string>(data, 'name')} className="mb-8" />

          {/* Rank history for this storefront (SPEC 06) and the worldwide
              trailing-month estimates (SPEC 04) as one card with one view
              switch; not rendered when the app has neither. */}
          {hydrated ? <AppPerformanceCard appId={currentAppId} countryCode={currentCountry} className="mb-8" /> : <PerformanceCardLoading />}

          {/* App Store search positions. Same non-blocking, self-hiding contract as the
              ads section.

              lg:hidden because from lg up this panel renders in the right-hand
              column under the globe (app-detail-view.tsx), and that column is
              itself `hidden lg:flex`. The two mounts never show at the same
              width, and they share one react-query cache, so this is one fetch
              either way. */}
          <AppKeywordRanks
            appId={currentAppId}
            className="mb-8 lg:hidden"
          />

          {/* Live App Store reviews with a cached theme summary. */}
          <AppReviewsSection appId={currentAppId} country={currentCountry} className="mb-8" />

          {/* One Ratings card: Global or this storefront, average, count and new ratings a day (SPEC 07 + 25). */}
          <AppRatingsCard appId={currentAppId} country={currentCountry} className="mb-8" />

          {/* Every price this app has charged here, and the changes between them (SPEC 26). */}
          <AppPriceHistory appId={currentAppId} country={currentCountry} className="mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <AppWhatsNew
              releaseNotes={getAppField<string>(data, 'release_notes') || (data as any)?.releaseNotes}
              version={getAppField<string>(data, 'version')}
              currentVersionReleaseDate={getAppField<string>(data, 'current_version_release_date')}
              isLoading={isLoadingMetadata}
            />
            <AppInformation
              provider={getAppField<string>(data, 'developer_name') || ""}
              developerUrl={getAppField<string>(data, 'developer_website') || overview?.websiteUrl}
              version={overview?.version ?? getAppField<string>(data, 'version')}
              updatedAt={overview?.updatedAt ?? updRaw}
              releaseDate={overview?.releaseDate ?? relRaw}
              countryReleaseDate={overview?.countryReleaseDate ?? null}
              countryCode={currentCountry}
              publisherCountry={overview?.publisherCountry ?? null}
              size={getAppField<number>(data, 'size_bytes') || (data as any)?.fileSizeBytes}
              category={getAppField<string>(data, 'primaryGenreName') || getCategoryNameById(parseInt(getAppField<string>(data, 'category_id') || "0"))}
              compatibility={(data as any)?.minimumOsVersion}
              languages={getAppField<string[]>(data, 'languages')}
              ageRating={(data as any)?.contentAdvisoryRating || (data as any)?.trackContentRating}
              isLoading={isLoadingMetadata}
            />
          </div>


          <div className="mb-8">
            <h2 className={cn("mb-4", SECTION_TITLE)}>About this app</h2>
            <AppDescription description={getAppField<string>(data, 'description')} isLoading={isLoadingMetadata} />
          </div>

          {/* Every in-app purchase Apple lists, last on the page (owner 2026-09-18, tasks.md:1160). */}
          <AppInAppPurchases appId={currentAppId} country={currentCountry} className="mb-8" />
        </>
      )}

      {/* Competitors tab content (mobile only) */}
      {isMobile && mobileTab === 'competitors' && (
        <div className="-mx-4 -mt-4 mb-24">
          <AppDetailSidebar appId={currentAppId} country={currentCountry} mode="similar" locked />
        </div>
      )}

      {/* Rankings tab content (mobile only) */}
      {isMobile && mobileTab === 'rankings' && (
        <div className="-mx-4 -mt-4 mb-24">
          <AppDetailSidebar appId={currentAppId} country={currentCountry} mode="country" locked />
        </div>
      )}

      {/* Bottom mobile tab bar */}
      {isMobile && (
        <>
          <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden pointer-events-none">
            <div className="pointer-events-auto mx-4 mb-[calc(1rem+env(safe-area-inset-bottom))]">
              <LiquidGlassDock withTopFade>
                <div className="grid grid-cols-3 px-2 py-1.5">
                  {(
                    [
                      { key: "info" as const, label: "Info", Icon: Smartphone },
                      { key: "competitors" as const, label: "Competitors", Icon: Swords },
                      { key: "rankings" as const, label: "Rankings", Icon: Globe },
                    ] as const
                  ).map(({ key, label, Icon }) => {
                    const active = mobileTab === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={cn(
                          "relative w-full flex flex-col items-center justify-center gap-0.5 text-[11px] leading-none py-1 rounded-2xl transition-all duration-200 active:scale-[0.98]",
                          active ? "text-foreground" : "text-muted-foreground"
                        )}
                        onClick={() => setMobileTab(key)}
                      >
                        {active ? (
                          <span className="absolute inset-0 rounded-2xl bg-white/70 border border-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] dark:bg-white/10 dark:border-white/10 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />
                        ) : null}

                        <span className="relative">
                          <Icon
                            className={cn("h-[18px] w-[18px] transition-transform duration-200", active && "scale-110")}
                            strokeWidth={active ? 2.5 : 2}
                          />
                          {active ? (
                            <span className="absolute -inset-1 rounded-full blur-md bg-black/10 dark:bg-white/10" />
                          ) : null}
                        </span>
                        <span className="relative font-medium">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </LiquidGlassDock>
            </div>
          </div>
          <LiquidGlassDockGlobalStyles />
        </>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-300 text-sm">
          <p className="font-medium mb-1">Error loading complete app details:</p>
          <p>{error}</p>
        </div>
      )}
      {shareUsed && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={typeof window !== 'undefined' ? window.location.href : ''} />}
    </>
  )
}

export default AppDetailMain;
